import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ComuneInfoDTO } from '../../shared/models/pratiche';
import { ComuniApiService } from './comuni-api.service';
import { PraticheApiService } from './pratiche-api.service';
import { TipoPraticaApiService } from './tipo-pratica-api.service';
import { LocalStorageService } from '../local-storage.service';
import { GlobalManagerPraticheService } from './global-manager-pratiche.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalManagerComuneService {

  comuni$ = new BehaviorSubject<ComuneInfoDTO[]>([]);
  comuneSelezionato$ = new BehaviorSubject<ComuneInfoDTO | null>(null);

  constructor(
    private comuniApiService: ComuniApiService,
    private praticheApiService: PraticheApiService,
    private tipoPraticaApiService: TipoPraticaApiService,
    private localStorageService: LocalStorageService,
    private globalManagerPraticheService: GlobalManagerPraticheService,
  ) {}

  loadComuni$(): Observable<ComuneInfoDTO[]> {
    return this.comuniApiService.getComuni().pipe(
      tap(comuni => this.comuni$.next(comuni)),
    );
  }

  /**
   * Dopo loadComuni$, prova a ripristinare il comune dal localStorage.
   * Se mono-comune, auto-seleziona. Se slug salvato valido, lo usa.
   * Ritorna true se il comune è stato ripristinato, false altrimenti.
   */
  tryRestoreFromStorage$(): Observable<boolean> {
    const comuni = this.comuni;

    if (comuni.length === 1) {
      return this.selezionaComune$(comuni[0]).pipe(map(() => true));
    }

    return this.localStorageService.getSlugComune$().pipe(
      switchMap(slug => {
        if (!slug) return of(false);
        const match = comuni.find(c => c.slugComune === slug);
        if (!match) return of(false);
        return this.selezionaComune$(match).pipe(map(() => true));
      }),
    );
  }

  /**
   * Seleziona un comune: setta slug nei servizi, salva in LS, carica pratiche utente.
   * Ritorna l'Observable della chain completa.
   */
  selezionaComune$(comune: ComuneInfoDTO): Observable<any> {
    const slug = comune.slugComune!;
    this.comuneSelezionato$.next(comune);
    this.praticheApiService.setSlugComune(slug);
    this.tipoPraticaApiService.setSlugComune(slug);

    return this.localStorageService.setSlugComune$(slug).pipe(
      switchMap(() => this.globalManagerPraticheService.loadPraticheMensa$().pipe(
        catchError(err => {
          console.error('[API] ERROR loadPraticheMensa in selezionaComune$, continuing...', err);
          return of([]);
        }),
      )),
    );
  }

  get comuneSelezionato(): ComuneInfoDTO | null {
    return this.comuneSelezionato$.getValue();
  }

  get comuni(): ComuneInfoDTO[] {
    return this.comuni$.getValue();
  }

  get isMultiComune(): boolean {
    return this.comuni.length > 1;
  }

  get hasComuneSelezionato(): boolean {
    return this.comuneSelezionato !== null;
  }

  reset() {
    this.comuneSelezionato$.next(null);
    this.globalManagerPraticheService.reset();
  }
}
